// Page-scoped glyph set for the /home dashboard. The Jmix view used `VaadinIcon`s; here we render small
// inline SVGs (24×24, stroke `currentColor`) so the tones inherit from the `.fcc-*` containers — kept
// local to this page (additive; no shared `@fawkes/ui` component is touched). Names mirror the Jmix
// VaadinIcon usages 1:1.

import type { ReactNode } from "react";

const PATHS: Record<string, ReactNode> = {
  // VaadinIcon.PLUS
  plus: <path d="M12 5v14M5 12h14" />,
  // VaadinIcon.SIGN_IN
  "sign-in": (
    <>
      <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
      <path d="M10 17l5-5-5-5M15 12H3" />
    </>
  ),
  // VaadinIcon.SIGN_OUT
  "sign-out": (
    <>
      <path d="M9 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h4" />
      <path d="M16 17l5-5-5-5M21 12H9" />
    </>
  ),
  // VaadinIcon.CLIPBOARD_CHECK
  "clipboard-check": (
    <>
      <path d="M9 4h6a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z" />
      <path d="M16 5h2a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2" />
      <path d="M9 14l2 2 4-4" />
    </>
  ),
  // VaadinIcon.HEART
  heart: (
    <path d="M12 20s-7-4.5-9.5-9C1 8 2.5 4.5 6 4.5c2 0 3 1 4 2.5 1-1.5 2-2.5 4-2.5 3.5 0 5 3.5 3.5 6.5C19 15.5 12 20 12 20z" />
  ),
  // VaadinIcon.INVOICE
  invoice: (
    <>
      <path d="M6 2h9l5 5v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z" />
      <path d="M8 9h6M8 13h8M8 17h5" />
    </>
  ),
  // VaadinIcon.SHIELD
  shield: <path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6l7-3z" />,
  // VaadinIcon.DROP
  drop: <path d="M12 3s6 6.5 6 11a6 6 0 0 1-12 0c0-4.5 6-11 6-11z" />,
  // VaadinIcon.WARNING
  warning: (
    <>
      <path d="M12 3l10 18H2L12 3z" />
      <path d="M12 10v5M12 18h.01" />
    </>
  ),
  // VaadinIcon.CLOCK
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  // VaadinIcon.FILE_TEXT_O
  "file-text": (
    <>
      <path d="M7 3h7l5 5v12a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" />
      <path d="M14 3v5h5M9 13h6M9 17h6" />
    </>
  ),
  // VaadinIcon.CALENDAR
  calendar: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 9h18M8 3v4M16 3v4" />
    </>
  ),
  // VaadinIcon.CHART
  chart: (
    <>
      <path d="M4 4v16h16" />
      <path d="M8 16v-4M12 16V8M16 16v-6" />
    </>
  ),
  // VaadinIcon.USERS
  users: (
    <>
      <circle cx="9" cy="8" r="3" />
      <path d="M3 20c0-3 3-5 6-5s6 2 6 5" />
      <path d="M16 5.5a3 3 0 0 1 0 5.5M17 15c2 .5 4 2 4 5" />
    </>
  ),
  // VaadinIcon.EXCHANGE
  exchange: (
    <>
      <path d="M4 8h13l-3-3M20 16H7l3 3" />
    </>
  ),
  // VaadinIcon.COG
  cog: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M19 5l-2 2M7 17l-2 2" />
    </>
  ),
  // VaadinIcon.ARROW_RIGHT
  "arrow-right": <path d="M5 12h14M13 6l6 6-6 6" />,
};

export function DashIcon({ name, className }: { name: string; className?: string }): ReactNode {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      width="24"
      height="24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {PATHS[name] ?? null}
    </svg>
  );
}
